import React, { useState } from "react";

import { Typography } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import { useNotification } from "Containers/Notifications";
import { Button, ButtonWrapper } from "ds/Button";
import Modal from "ds/Modal";
import { useDeleteMemberProject } from "Reducers/editingMember/memberProjects/hooks/useDeleteMemberProject";
import { useInvalidateMemberProjectAccess } from "Reducers/editingMember/memberProjects/memberProjects.slice";

type Props = {
  organizationId: string;
  projectId: string;
  projectName: string;
  userId: string;
  userName: React.ReactNode;
  // This can be used when the userName is yourself. the current notification
  // title uses this prop to show the full user name instead of "yourself".
  fullUserName?: React.ReactNode;
  onClose: () => void;
  onSuccess: () => void;
};

export const LeaveProjectModal = (props: Props) => {
  const notify = useNotification();
  const { action: deleteProcessAccess } = useDeleteMemberProject(props.userId);
  const invalidateMemberProjectAccess = useInvalidateMemberProjectAccess();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async () => {
    setIsSubmitting(true);
    await deleteProcessAccess(props.projectId);
    invalidateMemberProjectAccess(`${props.userId}:${props.organizationId}`);

    const values = {
      projectTitle: props.projectName || "",
      userName: props.userName || "",
      strong: (...children) => <strong>{children}</strong>
    };
    notify.success({
      title: (
        <FormattedMessage
          id="project.access.leave.success.title"
          values={{
            ...values,
            userName: props.fullUserName ? props.fullUserName : props.userName
          }}
        />
      ),
      description: (
        <FormattedMessage
          id="project.access.leave.success.description"
          values={values}
        />
      )
    });
    props.onSuccess();
    props.onClose();
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen onOpenChange={props.onClose}>
      <Typography.Title tag="h3">
        <FormattedMessage id="project.access.leave" />
      </Typography.Title>

      <Typography.BodyText>
        <FormattedMessage
          id="project.access.leave.confirm"
          values={{
            projectTitle: props.projectName,
            userName: props.userName,
            strong: (...children) => <strong>{children}</strong>
          }}
        />
      </Typography.BodyText>

      <ButtonWrapper spacing="modal" justifyContent="end">
        <Button variant="secondary" analyticId="cancel" onClick={props.onClose}>
          <FormattedMessage id="cancel" />
        </Button>
        <Button onClick={onSubmit} analyticId="yes" disabled={isSubmitting}>
          <FormattedMessage id="yes" />
        </Button>
      </ButtonWrapper>
    </Modal>
  );
};
